/**
 * 这个页面全部签文的唯一真实来源,server 与 client 共用。
 * (结构与 app/funny-fortune-cookie-messages/messages-data.ts 一致。)
 *
 * ## 为什么建这一页
 *
 * DataForSEO 实测(2026-08):love/romantic 簇按词集去重后 18 个真实查询、
 * 可寻址 3,700/月,而本站只占到 310/月(9%)。缺口 3,390/月,几乎全是 KD 0。
 * 对比之下 motivational 全簇只有 800/月 —— 那个方向被数据否掉了,这个没有。
 *
 * `relationship fortune cookie`(720/月)是簇里最大的词,建页前专门查过它的
 * SERP 确认意图:第一名是 "37 Fortune Cookie Sayings About Love",第二、四、
 * 十名分别是 Sweet Expressions of Love / Romantic Fortune Cookie Messages /
 * Romantic Valentine Fortune Cookies —— 清一色语录清单,不是别的意图。榜首
 * 只有 37 条。
 *
 * ## 与既有页面的分工(这个站有自残前科,必须写清楚)
 *
 * 首页已经排着 `love fortune cookie online` #25、`fortune cookie online love`
 * #27、`free love fortune cookie` #36 —— 全都带 online/free,是**工具意图**
 * (想在线抽一张爱情签)。本页一个都不碰,只吃 sayings / quotes / messages 这类
 * **内容意图**的词。
 *
 * /funny-fortune-cookie-messages 里有一组 "Funny Fortunes About Love and
 * Friendship" —— 那是**搞笑向**的爱情签。本页是真诚向的,标题和分组里一律不出现
 * "funny",避免两页争同一批词。守卫见
 * __tests__/app/love-messages-content.test.ts。
 */

export interface LoveMessageGroup {
  id: string;
  /** 渲染成 <h2>,每个挂一个具体目标词,见各组注释 */
  heading: string;
  blurb: string;
  navLabel: string;
  messages: string[];
}

export const loveMessageGroups: LoveMessageGroup[] = [
  {
    // 目标词: love fortune cookie sayings (260)
    id: "new-romance",
    heading: "Love Fortune Cookie Sayings for New Romance",
    navLabel: "New Romance",
    blurb:
      "Love fortune cookie sayings for the part where you are not sure yet. Short enough for a paper slip, honest enough to be worth handing over.",
    messages: [
      "Someone is about to become the reason you check your phone.",
      "The next person who makes you laugh twice in an hour is worth a second look.",
      "You will meet somebody who asks a follow-up question. Keep them.",
      "A small kindness this week begins something longer than you expect.",
      "You will be nervous. Go anyway.",
      "The right person makes you feel less like performing and more like resting.",
      "Say the thing. The worst outcome is a shorter wait.",
      "Somebody remembers what you said in passing. That is the whole signal.",
      "A door opens for you this month. There is a person behind it.",
      "You are not too much. You have been asking the wrong people.",
      "The beginning will feel ordinary. That is how the good ones start.",
      "Do not rehearse. They already like the unrehearsed version.",
    ],
  },
  {
    // 目标词: love fortune cookie quotes (260)
    id: "long-term",
    heading: "Romantic Fortune Cookie Quotes for Long-Term Couples",
    navLabel: "Long-Term Love",
    blurb:
      "Romantic fortune cookie quotes for people who are years in and still choosing it. Less fireworks, more Tuesday.",
    messages: [
      "Love is not one grand gesture. It is ten thousand small ones nobody sees.",
      "You will argue about the thermostat for decades. Choose warmth.",
      "The person who still asks how your day went is still choosing you.",
      "A long love is two people forgiving each other on a rolling basis.",
      "You will finish their sentence wrong and be corrected fondly.",
      "The strongest bond is built on ordinary Tuesdays, not on anniversaries.",
      "Keep dating the person you already have.",
      "Years from now you will miss exactly this, so look up.",
      "Comfortable is not the opposite of romantic. It is the reward for it.",
      "You will both keep growing. Grow toward, not away.",
      "The best conversations of your life are with somebody in the next room.",
      "Say the compliment out loud. They cannot hear you thinking it.",
    ],
  },
  {
    // 目标词: relationship fortune cookie (720) —— 全簇最大的词
    //        fortune cookie sayings about relationships (90)
    id: "relationships",
    heading: "Fortune Cookie Sayings About Relationships",
    navLabel: "Relationships",
    blurb:
      "Fortune cookie sayings about relationships that sound like advice because they are. Read one out loud and watch somebody go quiet.",
    messages: [
      "A relationship is not a test you pass once.",
      "You will be understood if you are willing to be clear.",
      "The problem is rarely the dishes. Talk about the thing under the dishes.",
      "Choose somebody whose bad days you can stand, not only their good ones.",
      "Listening is louder than agreeing.",
      "Do not keep score. Nobody wins that game and both people lose.",
      "Ask for what you need before you resent them for not guessing.",
      "The right person makes you more yourself, not less.",
      "Every couple has the same argument for years. Learn to have it kindly.",
      "You cannot love somebody into changing. You can love who they already are.",
      "Say sorry first. It costs less than being right.",
      "A good relationship is boring in the best possible way.",
    ],
  },
  {
    // 目标词: fortune cookie valentine (260) / valentine's day fortune cookie (70)
    id: "valentines",
    heading: "Valentine's Day Fortune Cookie Messages",
    navLabel: "Valentine's Day",
    blurb:
      "Valentine's Day fortune cookie messages for the card, the table setting, or the cookie you baked at eleven at night.",
    messages: [
      "Today is a good day to say it badly rather than not at all.",
      "The card matters less than the handwriting.",
      "Somebody is hoping you go first. Go first.",
      "Flowers wilt. What you said with them does not.",
      "Be specific this year. Generic praise lands like nothing.",
      "Your table for two is ready. So are you.",
      "Chocolate is a fine language. Words are a better one.",
      "Being somebody's ordinary Tuesday beats being their Valentine.",
      "Make the reservation. Regret is a worse dinner companion.",
      "Love is not a holiday, but a holiday is a decent excuse.",
      "Somebody is writing your name on something right now.",
      "The best Valentine gift is undivided attention. It is also the rarest.",
    ],
  },
  {
    // 目标词: fortune cookie wedding (110) / wedding fortune cookie favors (50)
    id: "wedding",
    heading: "Wedding Fortune Cookie Favors and Messages",
    navLabel: "Weddings",
    blurb:
      "Wedding fortune cookie favors that guests actually read out at the table. Print them, fold them, and let each place setting say something different.",
    messages: [
      "May your first argument be about something you laugh at later.",
      "Two people, one blanket, endless negotiation. Good luck.",
      "Today you promise. Tomorrow you practise.",
      "The marriage begins when the photographer leaves.",
      "May you always be each other's favourite person in a crowded room.",
      "You are not merging two lives. You are building a third.",
      "Take the picture. Eat the cake. Remember the small parts.",
      "May you argue well and forgive quickly.",
      "The best marriages are two people being unreasonably kind on purpose.",
      "Somebody in this room is already crying. That is allowed.",
      "May your love outlast your wedding playlist.",
      "Here is to a lifetime of splitting the last of everything.",
    ],
  },
  {
    // 目标词: fortune cookie love messages (110)
    id: "sweet-texts",
    heading: "Sweet Fortune Cookie Love Messages to Send",
    navLabel: "Sweet & Short",
    blurb:
      "Fortune cookie love messages short enough to text. Copy one and send it with no explanation — that is the whole charm.",
    messages: [
      "Thinking about you at an inconvenient time again.",
      "You are my favourite notification.",
      "I hope today is being gentle with you.",
      "No reason. Just wanted to be in your phone for a second.",
      "You would have laughed at this. That is why I am telling you.",
      "Come home. The good chair is empty.",
      "I like you in a way that is becoming inconvenient.",
      "You are the plan. Everything else is logistics.",
      "Saw something small and thought of you. That is most days.",
      "Missing you is a full-time job with no benefits.",
      "You make ordinary hours worth reporting on.",
      "Get here safely. I have things to tell you.",
    ],
  },
  {
    // 目标词: fortune cookie about love (390) / love fortune cookie (390)
    id: "falling",
    heading: "Fortune Cookie Quotes About Falling in Love",
    navLabel: "Falling in Love",
    blurb:
      "A fortune cookie about love should tell you something you already suspected. These are for the stretch where you are still pretending you are fine.",
    messages: [
      "It will happen gradually, then embarrassingly fast.",
      "You will notice their hands before you notice you are in trouble.",
      "Falling in love is mostly paying attention on purpose.",
      "One day you will realise it already happened weeks ago.",
      "You will start saving stories to tell one specific person.",
      "The moment you stop performing is the moment it becomes real.",
      "You will be scared. That is how you know it counts.",
      "Love arrives disguised as wanting to share your food.",
      "Your standards did not lower. Your understanding widened.",
      "You will reread a message you already know by heart.",
      "It starts as curiosity and ends as a habit you protect.",
      "You will be extremely obvious and think you are being subtle.",
    ],
  },
  {
    id: "anniversary",
    heading: "Anniversary Fortune Cookie Messages",
    navLabel: "Anniversaries",
    blurb:
      "Anniversary fortune cookie messages for the dinner where neither of you can remember which year it is.",
    messages: [
      "Another year of choosing the same person on purpose.",
      "You have now spent enough time together to share one personality.",
      "The first year is a story. The tenth is a language.",
      "Count what you built, not what you survived.",
      "You are somebody's oldest inside joke.",
      "Every year this gets less impressive and more remarkable.",
      "Toast the boring years. Those were the good ones.",
      "You have argued about directions in four countries. Here is to more.",
      "Anniversaries are receipts. Yours are getting long.",
      "The same person, a different year, a better version of you both.",
      "You still text them from the next room. Never stop.",
      "Here is to the parts nobody else sees.",
    ],
  },
  {
    id: "crush",
    heading: "Fortune Cookie Sayings for a Crush or a First Date",
    navLabel: "Crush & First Date",
    blurb:
      "For the stage where everything is a signal. Slip one across the table and let it do the talking.",
    messages: [
      "They noticed. They are waiting for you to notice they noticed.",
      "You will overthink the first message. Send the second draft.",
      "Wear the thing that makes you stand up straighter.",
      "Ask about the thing they lit up about last time.",
      "This is going better than you think it is.",
      "Silence is not failure. Let one happen.",
      "You will replay tonight for a week. Give yourself something good.",
      "Be early, be curious, and be honest about the dessert.",
      "Somebody is telling their friend about you right now.",
      "Say your real opinion about the film. That is the test.",
      "The nerves mean you care, not that you will fail.",
      "Text first. The rule about waiting was invented by nobody.",
    ],
  },
  {
    // 这一组直接来自实测 PAA:"Will you be my girlfriend fortune cookie"
    // 是 Google 在 `relationship fortune cookie` 底下真实展示的问题。
    id: "be-mine",
    heading: "Will You Be Mine: Fortune Cookie Messages That Ask",
    navLabel: "Will You Be Mine",
    blurb:
      "Somebody searches for this every month: a fortune cookie that asks the question for you. Print one, bake it in, and hand it over.",
    messages: [
      "Will you be my person? Circle yes.",
      "This cookie has a question. I have been carrying it for weeks.",
      "Be mine, and I will keep making you these terrible cookies.",
      "I practised this in the car. Will you be my girlfriend?",
      "Say yes and I will explain the rest over dinner.",
      "Two words fit inside a fortune cookie: be mine.",
      "Will you be my boyfriend? I have run out of subtle.",
      "Look up. That is me, asking.",
      "The real fortune is behind you, on one knee.",
      "I would like to keep doing this with you. All of it.",
      "This is not a fortune. It is a request.",
      "Yes or no, I am glad I asked.",
    ],
  },
];

/**
 * 页面上真实渲染的签文总数。metadata 和 H1 里的数字都从这里取,不许手写 ——
 * 本站踩过「标题写 100+、页面只有 16 条」的坑,守卫见
 * __tests__/app/love-messages-content.test.ts。
 */
export const totalLoveMessageCount = loveMessageGroups.reduce(
  (sum, group) => sum + group.messages.length,
  0,
);
